import { colorsPalettes } from "@similarweb/styles";

import { LeadersHighestLowestCell } from "app/components/React/Table/cells/LeadersHighestLowestCell";
import {
    abbrNumberVisitsWithNullFilter,
    abbrNumberVisitsWithNullOrUndefinedFilter,
    swNumberFilter,
    timeFilter,
    percentageSignFilter,
} from "app/filters/ngFilters";
import {
    ETooltips,
    getTooltipConfigs,
} from "app/pages/app-intelligence/app-analysis/pages/Engagement/config/tooltipBelowTheGraphConfig";
import { getNumberFilter, getHasPremiumPlusClaim } from "app/pages/app-intelligence/helpers";
import type { IEngagementAndUsageMetricsMetaData } from "app/pages/app-intelligence/types";
import { visitsFormatter } from "pro-features/pages/segments/components/benchmarkOvertime/utils";

import { TAB_NAMES } from "../constants";

const googlePlusTabs = Object.values(TAB_NAMES);
const usagePlusTabs = ["AvgSessionsPerUser", "AvgUsageTimePerSession", "AvgTotalUsageTimePerUser"];

//TODO:
// Remake to simple config object
// remove API urls to separate place
// remove all filtering to separate functions respectfully

export const getGoogleChartTableConfig = (isUsageWidget?: boolean, isNewGoogle?: boolean, isBeta?: boolean) => {
    const hasPremiumPlusClaim = getHasPremiumPlusClaim();

    return {
        pageSection: "Usage",
        pageName: "Engagement",
        meta: [
            {
                title: "apps.usage.usersengagement.tabs.monthlyactiveusers.title",
                dataKey: "Monthly Active Users", //used in the png service
                name: "MonthlyActiveUsers", //used to get the data as in the api
                metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
                icon: "month",
                metric: "AppEngagementMonthlyActiveUsers", //used in the dashboards
                dashboardWidgetType: "AppEngagementOverviewTable",
                filter: (val) => visitsFormatter()(val),
                yAxisFilter: getNumberFilter(),
                tabFilterNum: abbrNumberVisitsWithNullOrUndefinedFilter(),
                tooltipsArray: [getTooltipConfigs().get(ETooltips.Similar_Algorithm_Change_Date)],
                infoIcon: "apps.engagementoverview.tabs.mau.tooltip.ud",
                hideGranularitySelector: true,
                withTableTooltip: true,
                labelColor: isBeta && colorsPalettes.mint["500"],
                labelText: isBeta && "beta_label",
            },
            {
                title: "apps.usage.usersengagement.tabs.weekly_active_users.title",
                dataKey: "Weekly Active Users",
                name: TAB_NAMES.WeeklyActiveUsers,
                isPremiumPlusExclusive: true,
                metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
                icon: "weekly-active-users",
                timeGranularity: "Weekly",
                metric: "AppEngagementWeeklyActiveUsers",
                dashboardWidgetType: "AppEngagementOverviewTable",
                yAxisFilter: getNumberFilter(),
                tabFilterNum: abbrNumberVisitsWithNullOrUndefinedFilter(),
                filter: (val) => visitsFormatter()(val),
                tooltipsArray: [getTooltipConfigs().get(ETooltips.WEEKLY_DATA_AVAILABLE)],
                infoIcon: "apps.engagementoverview.new.ios.tabs.wau.tooltip",
                hideGranularitySelector: true,
                withTableTooltip: true,
                isNew: true,
                labelColor: isBeta ? colorsPalettes.mint["500"] : colorsPalettes.orange["400"],
                labelText: isBeta ? "beta_label" : "new.label.pill",
            },
            {
                title: "apps.usage.usersengagement.tabs.dailyactiveusers.title",
                dataKey: "Daily Active Users",
                name: "DailyActiveUsers",
                metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
                icon: "monthly-unique-visitors",
                metric: "AppEngagementDailyActiveUsers",
                dashboardWidgetType: "AppEngagementOverviewTable",
                yAxisFilter: getNumberFilter(),
                tabFilterNum: abbrNumberVisitsWithNullOrUndefinedFilter(),
                filter: (val) => visitsFormatter()(val),
                tooltipsArray: [getTooltipConfigs().get(ETooltips.Similar_Algorithm_Change_Date)],
                infoIcon: "apps.engagementoverview.tabs.dau.tooltip.ud",
                withTableTooltip: true,
            },
            {
                title: "apps.usage.usersengagement.tabs.daily.stickiness.title",
                dataKey: "Daily Stickiness",
                name: TAB_NAMES.DailyStickiness,
                isPremiumPlusExclusive: true,
                metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
                metric: "AppEngagementDailyStickiness",
                icon: "stickiness",
                timeGranularity: "Monthly",
                dashboardWidgetType: "AppEngagementOverviewTable",
                labelText: "new.label.pill",
                labelColor: colorsPalettes.orange["400"],
                yAxisFilter: percentageSignFilter(),
                tabFilterNum: percentageSignFilter(),
                filter: (val) => percentageSignFilter()(val),
                cellComponentTemplate: LeadersHighestLowestCell,
                infoIcon: "apps.engagementoverview.daily.stickiness.tooltip",
                hideGranularitySelector: true,
                isNew: true,
                withTableTooltip: true,
                isPercentage: true,
            },
            {
                title: "apps.usage.usersengagement.tabs.sessionsperusers.title",
                dataKey: "Sessions Per Users",
                name: "AvgSessionsPerUser",
                metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
                metric: "AppUsersEngagementAvgSessionsPerUser",
                icon: "employees",
                dashboardWidgetType: "AppEngagementOverviewTable",
                yAxisFilter: abbrNumberVisitsWithNullFilter(),
                tabFilterNum: swNumberFilter(),
                filter: (val) => swNumberFilter()(val, 2),
                cellComponentTemplate: LeadersHighestLowestCell,
                infoIcon: "apps.usage.usersengagement.tabs.sessionsperusers.tooltip",
                decimalPresentationTabTotal: 2,
                withTableTooltip: true,
                tooltipsArray: [getTooltipConfigs().get(ETooltips.Session_Tracking_Start_Date)],
            },
            {
                title: "apps.usage.usersengagement.tabs.avgsessiontime.title",
                dataKey: "Avg Session Time",
                name: "AvgUsageTimePerSession",
                metric: "AppUsersEngagementAvgUsageTimePerSession",
                metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.time",
                icon: "avg-visit-duration",
                dashboardWidgetType: "AppEngagementOverviewTable",
                filter: (val) => timeFilter()(val, null),
                yAxisFilter: timeFilter(),
                tabFilterNum: timeFilter(),
                cellComponentTemplate: LeadersHighestLowestCell,
                infoIcon: "apps.usage.usersengagement.tabs.avgsessiontime.tooltip",
                tooltipsArray: [getTooltipConfigs().get(ETooltips.Session_Tracking_Start_Date)],
                withTableTooltip: true,
            },
            {
                title: "apps.usage.usersengagement.tabs.totalsessiontime.title",
                dataKey: "Total Session Time",
                name: "AvgTotalUsageTimePerUser",
                metric: "AppUsersEngagementAvgTotalUsageTimePerUser",
                metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.time",
                icon: "avg-visit-duration",
                dashboardWidgetType: "AppEngagementOverviewTable",
                filter: (val) => timeFilter()(val, null),
                yAxisFilter: timeFilter(),
                tabFilterNum: timeFilter(),
                cellComponentTemplate: LeadersHighestLowestCell,
                infoIcon: "apps.usage.usersengagement.tabs.totalsessiontime.tooltip",
                tooltipsArray: [getTooltipConfigs().get(ETooltips.Session_Tracking_Start_Date)],
                withTableTooltip: true,
            },
        ].filter((config) => {
            if (isUsageWidget) {
                return usagePlusTabs.includes(config.name);
            }
            if (isNewGoogle) {
                return googlePlusTabs.includes(config.name);
            }
            if (hasPremiumPlusClaim) {
                return config;
            }
            return !config.isPremiumPlusExclusive;
        }),
        api: {
            overviewTable: "/widgetApi/AppEngagementOverview/AppEngagementUserOverview/Table",
            overTimeGraph: "/widgetApi/AppEngagementOverview/AppEngagementUserOverview/Data",
            excelLink: "/widgetApi/AppEngagementOverview/AppEngagementUserOverview/Excel",
        },
        appleGraphVertical: {
            name: "MonthlyActiveUsers",
            title: "apps.engagementoverview.tabs.mau.title",
        },
    };
};

export const getAppleChartTableConfig = (isBeta?: boolean): IEngagementAndUsageMetricsMetaData => ({
    pageSection: "Usage",
    pageName: "Engagement",
    meta: [
        {
            title: "apps.usage.usersengagement.tabs.monthlyactiveusers.title",
            dataKey: "Monthly Active Users",
            name: TAB_NAMES.MonthlyActiveUsers,
            metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
            icon: "month",
            timeGranularity: "Monthly",
            metric: "AppEngagementMonthlyActiveUsersIos",
            dashboardWidgetType: "AppEngagementOverviewTable",
            filter: (val) => visitsFormatter()(val),
            yAxisFilter: getNumberFilter(),
            tabFilterNum: abbrNumberVisitsWithNullOrUndefinedFilter(),
            tooltipsArray: [getTooltipConfigs().get(ETooltips.Similar_Algorithm_Change_Date)],
            infoIcon: "apps.engagementoverview.new.ios.tabs.mau.tooltip",
            hideGranularitySelector: true,
            withTableTooltip: true,
            labelColor: isBeta && colorsPalettes.mint["500"],
            labelText: isBeta && "beta_label",
        },
        {
            title: "apps.usage.usersengagement.tabs.weekly_active_users.title",
            dataKey: "Weekly Active Users",
            name: TAB_NAMES.WeeklyActiveUsers,
            isPremiumPlusExclusive: true,
            labelText: isBeta ? "beta_label" : "new.label.pill",
            labelColor: isBeta ? colorsPalettes.mint["500"] : colorsPalettes.orange["400"],
            metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
            icon: "weekly-active-users",
            timeGranularity: "Weekly",
            metric: "AppEngagementWeeklyActiveUsers",
            dashboardWidgetType: "AppEngagementOverviewTable",
            yAxisFilter: getNumberFilter(),
            tabFilterNum: abbrNumberVisitsWithNullOrUndefinedFilter(),
            filter: (val) => visitsFormatter()(val),
            tooltipsArray: [getTooltipConfigs().get(ETooltips.Similar_Algorithm_Change_Date)],
            infoIcon: "apps.engagementoverview.new.ios.tabs.wau.tooltip",
            hideGranularitySelector: true,
            withTableTooltip: true,
        },
        {
            title: "apps.usage.usersengagement.tabs.dailyactiveusers.title",
            dataKey: "Daily Active Users",
            name: TAB_NAMES.DailyActiveUsers,
            metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
            metric: "AppEngagementDailyActiveUsersIos",
            icon: "monthly-unique-visitors",
            timeGranularity: "Daily",
            dashboardWidgetType: "AppEngagementOverviewTable",
            labelText: isBeta ? "beta_label" : "new.label.pill",
            labelColor: isBeta ? colorsPalettes.mint["500"] : colorsPalettes.orange["400"],
            yAxisFilter: abbrNumberVisitsWithNullFilter(),
            tabFilterNum: abbrNumberVisitsWithNullOrUndefinedFilter(),
            filter: (val) => visitsFormatter()(val),
            cellComponentTemplate: LeadersHighestLowestCell,
            infoIcon: "apps.engagementoverview.new.ios.tabs.dau.tooltip",
            hideGranularitySelector: false,
            tooltipsArray: [getTooltipConfigs().get(ETooltips.Session_Tracking_Start_Date)],
            withTableTooltip: true,
        },
        {
            title: "apps.usage.usersengagement.tabs.daily.stickiness.title",
            dataKey: "Daily Stickiness",
            name: TAB_NAMES.DailyStickiness,
            isPremiumPlusExclusive: true,
            metricNameForGraphTooltip: "apps.usage.usersengagement.chart.tooltip.amount",
            metric: "AppEngagementDailyStickiness",
            icon: "stickiness",
            timeGranularity: "Monthly",
            dashboardWidgetType: "AppEngagementOverviewTable",
            labelText: "new.label.pill",
            labelColor: colorsPalettes.orange["400"],
            yAxisFilter: percentageSignFilter(),
            tabFilterNum: percentageSignFilter(),
            filter: (val) => percentageSignFilter()(val),
            cellComponentTemplate: LeadersHighestLowestCell,
            infoIcon: "apps.engagementoverview.daily.stickiness.tooltip",
            hideGranularitySelector: true,
            isNew: true,
            withTableTooltip: true,
            isPercentage: true,
        },
    ],
    api: {
        overviewTable: "widgetApi/AppEngagementOverview/AppEngagementUserActivity/Table",
        overTimeGraph: "widgetApi/AppEngagementOverview/AppEngagementUserActivity/Data",
        excelLink: "/widgetApi/AppEngagementOverview/AppEngagementUserActivity/Excel",
    },
    appleGraphVertical: {
        name: "MonthlyActiveUsers",
        title: "apps.engagementoverview.tabs.mau.title",
    },
});
